import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { clearFiltrosPersistidos } from '../shared/filtro-persistencia.util';

export interface User {
  id: number;
  corto: string;
  nombres: string;
  apellidos: string;
  usuario: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = '/api';
  private currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    if (this.getToken() && this.isTokenExpired()) {
      this.logout();
    }
  }

  login(usuario: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, { usuario, password })
      .pipe(
        tap(response => {
          // Al iniciar sesión, limpiar filtros persistidos para aplicar el default del usuario
          clearFiltrosPersistidos(response.user?.id);
          localStorage.setItem('token', response.token);
          localStorage.setItem('user', JSON.stringify(response.user));
          this.currentUserSubject.next(response.user);
          window.dispatchEvent(new Event('userLoggedIn'));
        })
      );
  }

  logout(): void {
    const uid = this.getUser()?.id;
    clearFiltrosPersistidos(uid);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.currentUserSubject.next(null);
  }

  handleUnauthorizedSession(): void {
    this.logout();
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getUser(): User | null {
    return this.currentUserSubject.value;
  }

  private getUserFromStorage(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      return null;
    }

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  isTokenExpired(token: string | null = this.getToken()): boolean {
    if (!token) {
      return true;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) {
        return false;
      }

      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  isAuthFailure(error: HttpErrorResponse): boolean {
    if (error.status === 401) {
      return true;
    }

    if (error.status !== 403) {
      return false;
    }

    const message = String(error.error?.error || '').toLowerCase();
    return (
      message.includes('token') ||
      message.includes('usuario no encontrado') ||
      message.includes('acceso requerido')
    );
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/auth/me`);
  }

  updatePassword(password: string, newPassword: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/auth/password`, { password, newPassword });
  }
}
